import { h, Component } from 'preact';
import Provider from 'preact-context-provider';
import wire from 'wiretie';
import { WeatherModel } from '../../model';
import Location from '@cube/location-widget';
import WeatherDefaultView from './weather';
import WeatherCompactView from './weather-compact';
import WeatherMiniView from './weather-mini';
import ErrorMessage from '../error-msg';
import WeatherSpinner from '../spinner';
import style from './style';
import delve from 'dlv';
import withConfig from '../../config';
import { appendTrackingParameters } from '../../lib/helpers';

export class ContainerDefault extends Component {

	state = {
		currentUnit: this.props.unit || 'F'
	};

	/**
	 * Update user's temperature scale preferences locally, and attempt to call
	 * a function named 'setUnits' if it exists on our props object. This props
	 * function currently saves this information to Local Storage and should flow down
	 * back to this component through it's model subscription.
	 *
	 * @param {string|undefined} unit - Temperature unit to set in Local Storage
	 */
	updateTempUnit = unit => {
		if (!unit || typeof unit !== 'string') {
			return;
		}

		if (!this.state.currentUnit || this.state.currentUnit.toUpperCase() !== unit.toUpperCase()){
			this.props.setUnit && this.props.setUnit(unit.toUpperCase());
			this.setState({ currentUnit: unit.toUpperCase() });
		}
	};

	componentWillReceiveProps(nextProps) {
		if (nextProps.unit && this.props.unit !== nextProps.unit) {
			this.setState({ currentUnit: nextProps.unit.toUpperCase() });
		}
	}

	render({
		location,
		locationServiceOrigin,
		notifyCurrentLocationChange,
		conditions,
		alerts,
		airQuality,
		weatherWidgetConfig: config,
		firstLoad,
		pending,
		rejected,
		displayMode,
		showFullForecastLink,
		showAlerts,
		theme,
		titleIcon
	}, { currentUnit }) {
		let content, header;

		if (rejected && rejected.conditions) {
			content = <ErrorMessage displayMode={displayMode} />;
		}
		else if (pending || firstLoad || !location || !location.latitude || !location.longitude) {
			content = <WeatherSpinner displayMode={displayMode} />;
		}
		else {
			const { trackingParams } = config;

			const originalWeatherLink = conditions.link ? conditions.link : config.weatherLink;
			const weatherLink = appendTrackingParameters(originalWeatherLink, trackingParams);

			if (displayMode === 'compact'){
				content = (
					<WeatherCompactView conditions={conditions} currentUnit={currentUnit}
						trackingParams={trackingParams} location={location} changeUnit={this.updateTempUnit}
						weatherLink={weatherLink} alerts={alerts} showAlerts={showAlerts} displayMode="compact"
						theme={theme}
					/>
				);
			}
			else if (displayMode === 'mini'){
				content = (
					<WeatherMiniView conditions={conditions} currentUnit={currentUnit} trackingParams={trackingParams}
						location={location} changeUnit={this.updateTempUnit} weatherLink={weatherLink} alerts={alerts}
						showAlerts={showAlerts} displayMode="mini" theme={theme}
					/>

				);
			}
			else {
				content = (
					<WeatherDefaultView conditions={conditions} currentUnit={currentUnit} weatherLink={weatherLink}
						trackingParams={trackingParams} alerts={alerts} changeUnit={this.updateTempUnit}
						showFullForecastLink={showFullForecastLink} showAlerts={showAlerts} theme={theme}
						airQuality={airQuality}
					/>
				);
			}
		}
		if (displayMode === 'mini' || displayMode === 'compact'){
			header = (<Location style="display: none" onChange={notifyCurrentLocationChange} locationServiceOrigin={locationServiceOrigin} />);
		}
		else {
			header = (
				<Location
					containerClass={style.location}
					popoverClass={style.popover}
					onChange={notifyCurrentLocationChange}
					anchor="right"
					locationServiceOrigin={locationServiceOrigin}
				/>);
		}
		return (
			<div class={style.weather}>
				{ header }
				{ content }
			</div>
		);
	}
}

export const Container = wire(
	'weather',
	({ location = {} }) => location.latitude && location.longitude ? {
		conditions: ['fetchCurrentConditions', location.latitude, location.longitude],
		airQuality: ['fetchAirQuality', location.latitude, location.longitude],
		alerts: ['fetchAlerts', { latitude: location.latitude, longitude: location.longitude }],
		unit: 'getTemperatureScale'
	} : {},
	weather => ({
		setUnit: weather.setTemperatureScale
	})
)( ContainerDefault );

export class WeatherDefault extends Component {
	state = {
		location: {},
		firstLoad: true // @TODO: Can this be fixed in location-widget|model?
	};

	/**
	 * Handle a new location being chosen by the location widget.
	 *
	 * There is a special case here for the first load or first time this
	 * handler is called, as it's often first given an empty object by the
	 * embedded location-widget/model. In this instance we do nothing but
	 * set our 'firstLoad' state to false which will allow any successive
	 * calls to this function with a bad {locationObject} value will be
	 * set into state and the error message handled by the <Container />.
	 *
	 * @param {Object} locationObject
	 * @param {string|Array|undefined} locationObject.postal_code
	 */
	notifyCurrentLocationChange = locationObject => {
		let data = {
			...locationObject,
			postal_code: [].concat(locationObject.postal_code)[0]
		};

		if (!data.postal_code && this.state.firstLoad) {
			this.setState({ firstLoad: false });
		}
		else {
			this.setState({ location: data, firstLoad: false });
		}
	};

	constructor(props, context) {
		super(props, context);

		let locationServiceOrigin = delve(props, 'locationServiceOrigin'),
			weatherServiceOrigin = delve(props, 'weatherServiceOrigin'),
			config = {};

		if (locationServiceOrigin) {
			config.locationServiceOrigin = locationServiceOrigin;
		}

		if (weatherServiceOrigin) {
			config.weatherServiceOrigin = weatherServiceOrigin;
		}

		this.weatherProvider = new WeatherModel(config);
	}

	render({ locationServiceOrigin, weatherWidgetConfig, ...props }, { location, firstLoad }) {
		return (
			<div>
				<Provider weather={this.weatherProvider}>
					<Container
						{...props}
						notifyCurrentLocationChange={this.notifyCurrentLocationChange}
						locationServiceOrigin={locationServiceOrigin}
						weatherWidgetConfig={weatherWidgetConfig}
						location={location}
						firstLoad={firstLoad}
					/>
				</Provider>
			</div>
		);
	}
}

export default withConfig([
	'locationServiceOrigin',
	'weatherServiceOrigin',
	'weatherWidgetConfig',
	'showAlerts'
])(WeatherDefault);
